import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, X, MapPin, Truck, CreditCard, Store, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { PageHead, Empty, money } from "@/components/common";
import { useCart } from "@/context/CartContext";
import AddressPicker from "@/components/AddressPicker";

const EMPTY_ADDR = { name: "", phone: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" };

function ShowGroup({ showId, showTitle, sellerName, items, onRemove }) {
  const [step, setStep] = useState("cart"); // cart | address | rate
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [warnings, setWarnings] = useState([]);
  const [rates, setRates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subtotal, setSubtotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const itemsTotal = items.reduce((s, l) => s + (l.price || 0), 0);
  const lotIds = items.map((l) => l.lot_id);

  const pay = async (rate) => {
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout-cart", {
        show_id: showId, lot_ids: lotIds, origin_url: window.location.origin,
        ship_to: addr, shipping_rate: rate || null,
      });
      window.location.href = res.checkout_url;
    } catch (err) { toast.error(err.message || "Couldn't start checkout"); setBusy(false); }
  };

  const validateThenRates = async (e) => {
    e.preventDefault();
    setBusy(true); setWarnings([]);
    try {
      const v = await api.post("/shipping/validate-address", { address: addr });
      if (!v.valid) { setWarnings(v.messages); setBusy(false); return; }
      const d = await api.post("/shipping/quote-cart", { show_id: showId, lot_ids: lotIds, ship_to: addr });
      setSubtotal(d.item_subtotal);
      if (d.requires_shipping === false) { await pay(null); return; }
      setRates(d.rates); setSelected(d.rates[0]?.rate_id); setStep("rate"); setBusy(false);
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  const rate = rates.find((r) => r.rate_id === selected);
  const total = subtotal + (rate?.buyer_charge || 0);

  return (
    <div className="ss-card" style={{ padding: 18 }} data-testid={`cart-group-${showId}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div className="stat-ic"><Store size={18} /></div>
        <div><div style={{ fontWeight: 800 }}>{sellerName || "Seller"}</div><div className="ss-muted" style={{ fontSize: 13 }}>{showTitle}</div></div>
        <div style={{ marginLeft: "auto", fontWeight: 800 }}>{money(itemsTotal)}</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        {items.map((l) => (
          <div key={l.lot_id} style={{ display: "flex", alignItems: "center", gap: 10 }} data-testid={`cart-line-${l.lot_id}`}>
            <img src={l.image} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }} />
            <div style={{ flex: 1, fontWeight: 700, fontSize: 14 }}>{l.title}</div>
            <div style={{ fontWeight: 800 }}>{money(l.price)}</div>
            {step === "cart" && <button className="disc-search" style={{ width: 28, height: 28 }} onClick={() => onRemove(l.lot_id)} data-testid={`cart-remove-${l.lot_id}`}><X size={14} /></button>}
          </div>
        ))}
      </div>

      {step === "cart" && (
        <button className="ss-btn ss-btn-primary ss-btn-block" onClick={() => setStep("address")} data-testid={`cart-checkout-${showId}`}>
          <ShoppingBag size={16} /> Checkout · one bundled shipping fee
        </button>
      )}

      {step === "address" && (
        <form onSubmit={validateThenRates} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <AddressPicker onChange={setAddr} warnings={warnings} testPrefix={`cart-${showId}`} initial={addr} />
          <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy} data-testid={`cartp-rates-${showId}`}><Truck size={16} /> {busy ? "Checking…" : "Get shipping rates"}</button>
        </form>
      )}

      {step === "rate" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>One bundled shipping fee</div>
          {rates.map((r) => (
            <label key={r.rate_id} className="ss-card" style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", borderColor: selected === r.rate_id ? "var(--brand)" : "var(--line)" }} data-testid={r.flat ? `cartp-rate-flat-${showId}` : undefined}>
              <input type="radio" checked={selected === r.rate_id} onChange={() => setSelected(r.rate_id)} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  {r.flat ? "Flat rate shipping" : `${r.carrier} · ${r.service}`}
                  {r.flat && <span className="ss-pill" style={{ background: "var(--gold)", color: "var(--brand-2)", fontSize: 11 }}>Set price</span>}
                </div>
                <div className="ss-muted" style={{ fontSize: 12 }}>{r.flat ? "One flat rate set by the seller" : r.delivery_window}</div>
              </div>
              <div style={{ fontWeight: 800 }}>{money(r.buyer_charge)}</div>
            </label>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 16 }}><span>Total</span><span data-testid={`cartp-total-${showId}`}>{money(total)}</span></div>
          <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy || !rate} onClick={() => pay(rate)} data-testid={`cartp-pay-${showId}`}><CreditCard size={16} /> {busy ? "Redirecting…" : `Pay ${money(total)}`}</button>
        </div>
      )}
    </div>
  );
}

export default function Cart() {
  const { items, remove } = useCart();
  const navigate = useNavigate();

  const groups = {};
  for (const it of items) {
    (groups[it.show_id] = groups[it.show_id] || { showTitle: it.show_title, sellerName: it.seller_name, items: [] }).items.push(it);
  }
  const keys = Object.keys(groups);

  return (
    <div className="fade-up" style={{ maxWidth: 720, margin: "0 auto" }}>
      <PageHead title="Your cart" sub="Items are saved across shows. Each seller ships together with one bundled fee." />
      {keys.length === 0 ? (
        <Empty icon={ShoppingBag} title="Your cart is empty" sub="Add Buy-Now items from any live show and they'll wait here." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {keys.map((sid) => (
            <ShowGroup key={sid} showId={sid} showTitle={groups[sid].showTitle} sellerName={groups[sid].sellerName} items={groups[sid].items} onRemove={remove} />
          ))}
          <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate("/")} style={{ alignSelf: "center" }} data-testid="keep-shopping">Keep shopping</button>
        </div>
      )}
    </div>
  );
}
